import { useEffect, useState } from 'react';

/**
 * A thumbnail URL for a picked file, while it is only in the browser.
 *
 * Images get an object URL, everything else (a PDF, a spreadsheet) gets
 * `undefined` so the caller can fall back to an icon. The URL is revoked when
 * the file changes or the component unmounts — an object URL keeps its blob
 * alive until it is, and a form where several photos are taken would otherwise
 * hold every one of them in memory.
 */
export const useImagePreviewUrl = (file?: File | null): string | undefined => {
  const [url, setUrl] = useState<string>();

  useEffect(() => {
    // `createObjectURL` is missing in jsdom, and outside a browser there is
    // nothing to preview anyway.
    if (!file?.type?.startsWith('image/') || typeof URL.createObjectURL !== 'function') {
      setUrl(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);

    // Guarded like the creation above: a test environment can take the API away
    // between render and unmount, and a throwing cleanup breaks the unmount.
    return () => {
      if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  return url;
};
